import { useAudioGeneration } from './features/podcast/hooks/useAudioGeneration';
import { useAuth } from './hooks/useAuth';
import { AuthError } from './hooks/useAuth';
import PodcastForm from './features/podcast/PodcastForm';
import AudioPlayer from './features/podcast/AudioPlayer';
import LoginModal from './components/LoginModal';
import Header from './components/Header';
import Profile from './components/Profile';
import { useState, useEffect } from 'react';
import { Modal } from './components/Modal';
import PodcastHistory, { PodcastHistoryItem } from './features/podcast/history/PodcastHistory';
import { authFetch } from './hooks/useAuth';

/**
 * Main Application Component
 *
 * Features:
 * - Text-to-speech podcast generation
 * - User authentication
 * - File upload support
 * - Audio playback
 *
 * The app follows a top-down state management approach where the main
 * app component orchestrates the authentication and audio generation states.
 */
const App: React.FC = () => {
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [profile, setProfile] = useState({
    email: 'test@example.com',
    displayName: 'Test User',
    stats: {
      podcastsGenerated: 5,
      lastGenerated: new Date().toISOString(),
    },
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [podcastHistory, setPodcastHistory] = useState<PodcastHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Custom hooks for core functionality
  const {
    audioSrc,
    generateAudio
  } = useAudioGeneration();

  const {
    isLoggedIn,
    isLoginModalOpen,
    isLoading,
    handleLogin,
    handleRegister,
    handleLogout,
    setIsLoginModalOpen,
    setIsLoggedIn
  } = useAuth();

  // Fetch podcast history when modal opens
  useEffect(() => {
    if (isHistoryOpen) {
      setHistoryLoading(true);
      setHistoryError(null);
      authFetch('/api/podcast_history')
        .then((data: any) => {
          // Map backend fields to PodcastHistoryItem
          const items = Array.isArray(data)
            ? data.map((p: any) => ({
                id: p.id,
                title: p.title,
                date: p.date_created,
                audioUrl: p.audio_url,
              }))
            : [];
          setPodcastHistory(items);
          console.log("items are ", items);
        })
        .catch((err) => {
          setHistoryError(err.message || 'Failed to load history');
        })
        .finally(() => setHistoryLoading(false));
    }
  }, [isHistoryOpen]);

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  /**
   * Handles the podcast generation submission
   */
  const handleSubmit = async (textInput: string, fileContent: string, fileType: string, deepResearch: boolean): Promise<void> => {
    setGenerationError(null);

    try {
      await generateAudio(textInput, fileContent, fileType, deepResearch);
    } catch (error) {
      if (error instanceof AuthError) {
        console.log('User needs to login:', error.message);
        setIsLoginModalOpen(true);
      } else {
        console.error('Audio generation failed:', error);
        setGenerationError(error instanceof Error ? error.message : 'An unexpected error occurred');
      }
    }
  };

  const handleAuthSuccess = () => {
    console.log('Auth success callback triggered');
    setIsLoggedIn(true);
  };

  const handleOpenProfile = () => {
    setIsProfileOpen(true);
    setProfileLoading(false);
    setProfileError(null);
  };

  const handleCloseProfile = () => setIsProfileOpen(false);

  const handleUpdateProfile = async (update: { displayName: string }) => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      // Simulate API delay
      await new Promise(res => setTimeout(res, 500));
      setProfile(prev => ({ ...prev, displayName: update.displayName }));
    } catch (e) {
      setProfileError('Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleOpenHistory = () => setIsHistoryOpen(true);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col relative">
      {/* Header Section */}
      <Header
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        isLoginModalOpen={isLoginModalOpen}
        onProfileClick={handleOpenProfile}
        onHistoryClick={handleOpenHistory}
      />

      {/* Main Content */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-6 md:py-10">
        {/* Title Section */}
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-violet-600">
          Podcast Generator
        </h1>
        <p className="text-gray-500 text-center mb-8 max-w-lg mx-auto">
          Transform your text into natural-sounding dialog podcasts with AI.
        </p>

        {/* Require login for features */}
        {!isLoggedIn ? (
          <LoginModal
            isOpen={!isLoggedIn}
            onClose={() => {}}
            onLogin={handleLogin}
            onRegister={handleRegister}
            onAuthSuccess={handleAuthSuccess}
          />
        ) : (
          <>
            {/* Form Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
              <PodcastForm onSubmit={handleSubmit} />
            </div>

            {/* Audio Player */}
            {audioSrc && (
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <AudioPlayer src={audioSrc} title="Generated Podcast" />
              </div>
            )}

            {/* Error Display */}
            {generationError && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-red-100">
                <div className="flex items-start space-x-3">
                  <div className="shrink-0 bg-red-50 rounded-lg p-2">
                    <svg
                      className="w-5 h-5 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      Generation Failed
                    </h3>
                    <p className="text-sm text-gray-500">
                      {generationError}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-400 text-sm">
        <p>© {new Date().getFullYear()} Podcast Generator. All rights reserved.</p>
      </footer>

      {/* Profile Modal */}
      {isProfileOpen && !isHistoryOpen && (
        <Modal isOpen={isProfileOpen} onClose={handleCloseProfile}>
          <Profile
            profile={profile}
            onUpdateProfile={handleUpdateProfile}
            loading={profileLoading}
            error={profileError}
            onLogout={() => { handleLogout(); handleCloseProfile(); }}
            onShowHistory={() => { setIsProfileOpen(false); setIsHistoryOpen(true); }}
          />
        </Modal>
      )}

      {/* Podcast History Modal */}
      {isHistoryOpen && (
        <Modal isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)}>
          {historyLoading ? (
            <div className="text-center py-8 text-gray-400">Loading history...</div>
          ) : historyError ? (
            <div className="text-center py-8 text-red-500">{historyError}</div>
          ) : (
            <PodcastHistory history={podcastHistory} />
          )}
        </Modal>
      )}
    </div>
  );
}

export default App;
